import React, { useContext } from "react";
import { cn, imageBuilder } from "../../lib/helpers";
import { CardContext } from "./context";
import { SectionContext } from "../sections/context";
import { isLinkedIn, isSlack } from "../sections/quote-panels";
import { LogoBox } from "../sections/logos";
import { ImageBuilder } from "../global/image-builder";
import { getAuthorPath } from "../../../helpers-new";

const AVATAR_SIZE = 48;

export const Byline = ({ maxAuthors, hideBorder, showLogo, skipCompany }) => {
  const card = useContext(CardContext);
  const section = useContext(SectionContext);
  const authors = maxAuthors ? card.authors?.slice(0, maxAuthors) : card.authors;

  // Check if any author is a speakerName type (simplified)
  const hasSimplifiedSpeakers = authors?.some(
    (item) => item._type === "speakerName" || (item.name && !item.author)
  );

  return authors ? (
    <ul className="flex flex-col gap-4">
      {authors.map((item, i) => {
        // If there are any simplified speakers, render ALL as simplified for consistency
        if (hasSimplifiedSpeakers) {
          const name = item.name || item.author?.name;
          return (
            <li
              className={cn(
                hideBorder ? "" : "border border-dark-10 rounded-xl p-2"
              )}
              key={`${card?.slug}-speaker-${name}-${i}`}
            >
              <div className="flex items-center min-h-[48px]">
                <div className="text-base leading-tight mx-2">
                  <p className="font-medium">{name}</p>
                </div>
              </div>
            </li>
          );
        }

        // Handle speakerName type (open-text name)
        if (item._type === "speakerName" || item.name) {
          return (
            <li
              className={cn(
                hideBorder ? "" : "border border-dark-10 rounded-xl p-2"
              )}
              key={`${card?.slug}-speaker-${item.name}-${i}`}
            >
              <div className="flex items-center min-h-[48px]">
                <div className="text-base leading-tight mx-2">
                  <p className="font-medium">{item.name}</p>
                </div>
              </div>
            </li>
          );
        }

        // Handle authorReference type with full details
        const hasAuthorPage = card?.linkToAuthor || (card.authorIdsWithPages && card.authorIdsWithPages.includes(item.author?._id));
        const authorHref = hasAuthorPage ? getAuthorPath(item.author?.name) : item.author?.url || item.author?.linkedin;
        const showAuthorLink = !card.skipAuthorLink && !card.url && item.author?.name && authorHref;
        return (
          <li
            key={`${card?.slug}-${item.author?.name}-${i}`}
          >
            {showAuthorLink ? (
              <a
                href={authorHref}
                {...(!hasAuthorPage && { target: "_blank", rel: "noopener noreferrer" })}
                className={
                  cn(
                    "block transform scale-100 hover:scale-101 transition-transform duration-200",
                    hideBorder ? "" : "border border-dark-10 rounded-xl p-2",
                    section.isDarkBg
                      ? "text-white hover:text-yellow-200"
                      : "text-black hover:bg-green-ultralight hover:border-green-emerald transition-all duration-200"
                  )}
              >
                <Author author={item.author} {...{ showLogo, skipCompany }} />
              </a>
            ) : (
              <Author author={item.author} {...{ showLogo, skipCompany }} />
            )}
          </li>
        );
      })}
    </ul>
  ) : null;
};

export const Author = ({ author, showLogo, skipCompany }) => {
  const card = useContext(CardContext);
  const section = useContext(SectionContext);

  // imageSrc used for override
  const hasImage = author?.imageSrc || author?.image?.asset;
  const isSocial = isLinkedIn(card.url) || isSlack(card.url);

  return author ? (
    <div
      className={cn(
        "flex justify-start",
        showLogo && author.companies ? "items-start" : "items-center"
      )}
    >
      {hasImage && (
        <ImageBuilder
          src={
            author.imageSrc ||
            imageBuilder(author.image)
              .width(AVATAR_SIZE * 2)
              .height(AVATAR_SIZE * 2)
              .quality(100)
              .auto("format")
              .fit("crop")
              .url()
          }
          alt={author.name}
          width={AVATAR_SIZE}
          height={AVATAR_SIZE}
          quality={100}
          className="shrink-0 rounded-full"
        />
      )}
      <div className="text-base leading-tight mx-2">
        <p className="font-medium">{author?.name || <em>Anonymous</em>}</p>
        <p
          className={cn(!section.isDarkBg ? "text-secondary" : "text-light-60")}
        >
          <span className="text-sm">
            {author?.title || "Contributor"}
            {!skipCompany && author.companies && author.companies[0] && (
              <>, {author.companies[0].title}</>
            )}
          </span>
        </p>
        {showLogo && author.companies && (
          <LogoBox
            image={author.companies[0].mainImage}
            maxHeight={52}
            left={true}
            alt={author.companies[0].title}
          />
        )}
      </div>
    </div>
  ) : null;
};
